/**
 * Late Fees Management Component (Phase 2)
 * Admin interface for applying and waiving late fees
 */

import React, { useState, useEffect } from 'react';
import {
  Container, Row, Col, Card, Button, Table, Badge, Alert, Modal, Form,
  Spinner, DropdownButton, Dropdown
} from 'react-bootstrap';
import { applyLateFees, waiveLateFee, getOverdueSubscriptions } from '../../services/billingPhase2Service';
import { Prohibit, Lightning, CheckCircle } from '@phosphor-icons/react';
import './LateFeeManagement.css';

export const LateFeeManagement = () => {
  const [loading, setLoading] = useState(false);
  const [subscriptions, setSubscriptions] = useState([]);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [showWaiveModal, setShowWaiveModal] = useState(false);
  const [selectedSub, setSelectedSub] = useState(null);
  const [waiverReason, setWaiverReason] = useState('Manual waiver');

  useEffect(() => {
    loadSubscriptionsWithLateFees();
  }, []);

  const loadSubscriptionsWithLateFees = async () => {
    setLoading(true);
    setError(null);

    try {
      const overdue = await getOverdueSubscriptions();
      setSubscriptions(overdue);
    } catch (err) {
      setError(err.error || 'Failed to load subscriptions');
    } finally {
      setLoading(false);
    }
  };

  const handleApplyLateFees = async () => {
    setLoading(true);
    setError(null);
    setSuccess(null);

    try {
      const result = await applyLateFees();
      setSuccess(`Successfully applied late fees to ${result.subscriptions_with_late_fees} subscriptions`);
      loadSubscriptionsWithLateFees();
    } catch (err) {
      setError(err.error || 'Failed to apply late fees');
    } finally {
      setLoading(false);
    }
  };

  const handleWaiverClick = (subscription) => {
    setSelectedSub(subscription);
    setWaiverReason('Manual waiver');
    setShowWaiveModal(true);
  };

  const handleWaiveSubmit = async () => {
    if (!selectedSub) return;

    setLoading(true);
    setError(null);
    setSuccess(null);

    try {
      const result = await waiveLateFee(selectedSub.subscription_id, waiverReason);
      setSuccess(`Late fee of GHS ${result.waived_amount} waived successfully`);
      setShowWaiveModal(false);
      loadSubscriptionsWithLateFees();
    } catch (err) {
      setError(err.error || 'Failed to waive late fee');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container fluid className="late-fee-management mt-4">
      {/* Header */}
      <Row className="mb-4">
        <Col>
          <h2 className="section-title"><Prohibit size={20} className="me-2" /> Late Fees Management</h2>
          <p className="text-muted">Monitor and manage late fees for overdue subscriptions</p>
        </Col>
        <Col md="auto">
          <Button
            variant="danger"
            onClick={handleApplyLateFees}
            disabled={loading}
            size="lg"
          >
            {loading ? (
              <>
                <Spinner animation="border" size="sm" className="me-2" />
                Processing...
              </>
            ) : (
              <>
                <Lightning size={18} className="me-2" />
                Apply Late Fees
              </>
            )}
          </Button>
        </Col>
      </Row>

      {/* Alerts */}
      {error && (
        <Alert variant="danger" dismissible onClose={() => setError(null)}>
          <Alert.Heading>Error</Alert.Heading>
          <p>{error}</p>
        </Alert>
      )}

      {success && (
        <Alert variant="success" dismissible onClose={() => setSuccess(null)}>
          <Alert.Heading>Success</Alert.Heading>
          <p>{success}</p>
        </Alert>
      )}

      {/* Summary Cards */}
      <Row className="mb-4">
        <Col md={3} className="mb-3">
          <Card className="summary-card">
            <Card.Body>
              <h6 className="text-muted mb-2">Subscriptions Overdue</h6>
              <h3 className="text-danger mb-0">{subscriptions.length}</h3>
            </Card.Body>
          </Card>
        </Col>
        <Col md={3} className="mb-3">
          <Card className="summary-card">
            <Card.Body>
              <h6 className="text-muted mb-2">With Late Fees Applied</h6>
              <h3 className="mb-0">
                {subscriptions.filter(s => s.late_fee_amount > 0).length}
              </h3>
            </Card.Body>
          </Card>
        </Col>
        <Col md={3} className="mb-3">
          <Card className="summary-card">
            <Card.Body>
              <h6 className="text-muted mb-2">Total Late Fees</h6>
              <h3 className="text-danger mb-0">
                GHS {subscriptions.reduce((sum, s) => sum + (s.late_fee_amount || 0), 0).toFixed(2)}
              </h3>
            </Card.Body>
          </Card>
        </Col>
        <Col md={3} className="mb-3">
          <Card className="summary-card">
            <Card.Body>
              <h6 className="text-muted mb-2">Avg Days Overdue</h6>
              <h3 className="mb-0">
                {subscriptions.length > 0
                  ? (subscriptions.reduce((sum, s) => sum + s.days_overdue, 0) / subscriptions.length).toFixed(0)
                  : '0'}
                {' days'}
              </h3>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Overdue Subscriptions Table */}
      <Row>
        <Col>
          <Card>
            <Card.Header className="bg-light">
              <h5 className="mb-0">Overdue Subscriptions Details</h5>
            </Card.Header>
            <Card.Body>
              {loading ? (
                <div className="text-center py-5">
                  <Spinner animation="border" role="status">
                    <span className="visually-hidden">Loading...</span>
                  </Spinner>
                </div>
              ) : subscriptions.length > 0 ? (
                <div className="table-responsive">
                  <Table hover>
                    <thead>
                      <tr>
                        <th>Invoice #</th>
                        <th>School ID</th>
                        <th>Due Date</th>
                        <th className="text-center">Days Overdue</th>
                        <th className="text-end">Outstanding</th>
                        <th className="text-end">Late Fee</th>
                        <th className="text-center">Status</th>
                        <th className="text-center">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {subscriptions.map((sub) => (
                        <tr key={sub.subscription_id}>
                          <td>
                            <small className="font-monospace">{sub.invoice_number || '—'}</small>
                          </td>
                          <td>
                            <small className="text-muted">{sub.school_id}</small>
                          </td>
                          <td>
                            <small>
                              {new Date(sub.due_date).toLocaleDateString()}
                            </small>
                          </td>
                          <td className="text-center">
                            <Badge
                              bg={sub.days_overdue > 30 ? 'danger' : 'warning'}
                              pill
                            >
                              {sub.days_overdue} days
                            </Badge>
                          </td>
                          <td className="text-end">
                            <strong>
                              GHS {sub.outstanding.toFixed(2)}
                            </strong>
                          </td>
                          <td className="text-end">
                            <span className="text-danger">
                              GHS {sub.late_fee_amount.toFixed(2)}
                            </span>
                          </td>
                          <td className="text-center">
                            <Badge
                              bg={sub.late_fee_amount > 0 ? 'danger' : 'warning'}
                            >
                              {sub.late_fee_amount > 0 ? 'Fee Applied' : 'Pending'}
                            </Badge>
                          </td>
                          <td className="text-center">
                            <DropdownButton
                              id={`dropdown-${sub.subscription_id}`}
                              title="Actions"
                              size="sm"
                              variant="outline-secondary"
                            >
                              <Dropdown.Item
                                onClick={() => handleWaiverClick(sub)}
                                className="text-success"
                                disabled={sub.late_fee_amount <= 0}
                              >
                                <CheckCircle size={16} className="me-2" />
                                Waive Fee
                              </Dropdown.Item>
                            </DropdownButton>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </div>
              ) : (
                <div className="alert alert-info" role="alert">
                  <strong><CheckCircle size={16} className="me-1" /> No overdue subscriptions!</strong> All schools are up to date.
                </div>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Waive Fee Modal */}
      <Modal show={showWaiveModal} onHide={() => setShowWaiveModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Waive Late Fee</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedSub && (
            <>
              <p>
                <strong>Invoice:</strong> {selectedSub.invoice_number || '—'}
              </p>
              <p>
                <strong>School:</strong> {selectedSub.school_id}
              </p>
              <p className="text-danger">
                <strong>Late Fee Amount:</strong> GHS{' '}
                {(selectedSub.late_fee_amount || 0).toFixed(2)}
              </p>

              <Form.Group className="mb-3">
                <Form.Label>Reason for Waiver</Form.Label>
                <Form.Control
                  as="select"
                  value={waiverReason}
                  onChange={(e) => setWaiverReason(e.target.value)}
                >
                  <option>Manual waiver</option>
                  <option>Financial hardship</option>
                  <option>Administrative error</option>
                  <option>System error</option>
                  <option>Goodwill gesture</option>
                  <option>Other</option>
                </Form.Control>
              </Form.Group>
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowWaiveModal(false)}>
            Cancel
          </Button>
          <Button
            variant="danger"
            onClick={handleWaiveSubmit}
            disabled={loading}
          >
            {loading ? (
              <>
                <Spinner animation="border" size="sm" className="me-2" />
                Processing...
              </>
            ) : (
              'Confirm Waiver'
            )}
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
};

export default LateFeeManagement;
